import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { USERAXIOS } from "../../api";
import { Language } from "../../types/resume";

interface LanguagesResponse {
  data: Language[];
  meta: {
    current_page: number;
    last_page: number;
    per_page: number;
    total: number;
  };
}

export const useLanguages = (page: number = 1, pageSize: number = 10) => {
  return useQuery({
    queryKey: ["languages", page, pageSize],
    queryFn: async (): Promise<LanguagesResponse> => {
      const response = await USERAXIOS.get<LanguagesResponse>("/languages", {
        params: {
          page,
          per_page: pageSize,
        },
      });
      return response.data;
    },
  });
};

export const useLanguagesInfinite = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  return useInfiniteQuery({
    queryKey: ["languages", "infinite", pageSize, searchTerm],
    queryFn: async ({ pageParam = 1 }): Promise<LanguagesResponse> => {
      const response = await USERAXIOS.get<LanguagesResponse>(`/languages`, {
        params: {
          page: pageParam,
          per_page: pageSize,
          title: searchTerm || undefined,
        },
      });
      return response.data;
    },
    getNextPageParam: (lastPage) => {
      return lastPage.meta.current_page < lastPage.meta.last_page
        ? lastPage.meta.current_page + 1
        : undefined;
    },
    initialPageParam: 1,
  });
};

export const useLanguagesForAutocomplete = (
  pageSize: number = 20,
  searchTerm: string = ""
) => {
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading,
    error,
    refetch,
  } = useLanguagesInfinite(pageSize, searchTerm);

  // Flatten all pages into a single array
  const languages = useMemo(() => {
    return data?.pages.flatMap((page) => page.data) || [];
  }, [data]);

  const loadMore = () => {
    if (hasNextPage && !isFetchingNextPage) {
      fetchNextPage();
    }
  };

  return {
    items: languages,
    loading: isLoading || isFetchingNextPage,
    hasMore: hasNextPage,
    loadMore,
    error,
    refetch,
    totalPages: data?.pages[0]?.meta.last_page || 0,
    totalItems: data?.pages[0]?.meta.total || 0,
  };
};
